import './styles/main.css';
import './styles/components.css';
import { ApplicationManager } from './js/applicationManager.js';
import { 
    showToast, 
    createApplicationCard, 
    updateStats 
} from './js/uiManager.js';
import { initializeModal } from './components/modal.js';
import { initializeDeleteConfirm } from './components/deleteConfirm..js';
import { formatDateForGoogleCalendar } from './js/utils.js';

// Initialize the app
document.addEventListener('DOMContentLoaded', () => {
    // Create the main app structure
    const appRoot = document.getElementById('app');
    if (!appRoot) return;
    appRoot.innerHTML = `
        <div class="container mx-auto p-4 md:p-8 max-w-6xl w-full">
            <header class="text-center mb-10 md:mb-16">
                <h1 class="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-100 tracking-tight">
                    Job Application Tracker
                </h1>
                <p class="text-slate-400 mt-3 text-lg md:text-xl max-w-2xl mx-auto">
                    Streamline your job search. Track applications, manage statuses, and stay organized.
                </p>
            </header>

            <div id="summaryStatsSection" class="mb-10 p-5 glassmorphic rounded-xl summary-stats">
                <div class="stat-item">
                    <div id="statTotalApplications" class="stat-value">0</div>
                    <div class="stat-label">Total Apps</div>
                </div>
                <div class="stat-item">
                    <div id="statInterviewing" class="stat-value">0</div>
                    <div class="stat-label">Interviewing</div>
                </div>
                <div class="stat-item">
                    <div id="statOffers" class="stat-value">0</div>
                    <div class="stat-label">Offers</div>
                </div>
                <div class="stat-item">
                    <div id="statAwaitingResponse" class="stat-value">0</div>
                    <div class="stat-label">Pending</div>
                </div>
            </div>

            <div class="text-center mb-8 hidden sm:block">
                <button id="openModalBtn" class="btn btn-primary text-lg">
                    <i class="ph-plus-circle"></i> Add New Application
                </button>
            </div>

            <div class="controls-container glassmorphic">
                <div class="controls-grid">
                    <div class="filter-input-group lg:col-span-1">
                        <i class="ph-magnifying-glass"></i>
                        <input type="search" id="searchInput" class="filter-input" placeholder="Search...">
                    </div>
                    <div class="flex items-center gap-2 lg:col-span-1">
                        <label for="statusFilter" class="filter-label">Status:</label>
                        <select id="statusFilter" class="filter-select w-full">
                            <option value="">All</option>
                            <option value="Applied">Applied</option>
                            <option value="Screening">Screening</option>
                            <option value="FollowUp">Followed Up</option>
                            <option value="Interviewing">Interviewing</option>
                            <option value="Thinking">Thinking</option>
                            <option value="Offer">Offer</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Ghosted">Ghosted</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-2 lg:col-span-1 justify-start sm:justify-center lg:justify-start">
                        <label class="filter-label">Sort Date:</label>
                        <button id="sortDateBtn" class="btn-icon" title="Toggle Sort Direction (Date Applied)" aria-label="Toggle Sort Direction by Date">
                            <i id="sortDateIcon" class="ph ph-sort-descending text-xl"></i>
                        </button>
                    </div>
                    <div class="lg:col-span-1 flex justify-start sm:justify-end">
                        <button id="exportJsonBtn" class="btn btn-secondary text-sm">
                            <i class="ph-download-simple"></i> Export JSON
                        </button>
                    </div>
                </div>
            </div>

            <div id="applicationList" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"></div>

            <div id="loadingIndicator" class="text-center py-10">
                <svg class="animate-spin h-8 w-8 text-sky-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="text-slate-400 mt-2 text-sm">Loading applications...</p>
            </div>

            <div id="noApplicationsMessage" class="empty-state-message hidden">
                <i class="ph-folder-notch-open"></i>
                <p>No applications tracked yet.</p>
                <p class="text-sm mt-2">Click the '+' button to add your first one!</p>
            </div>
            <div id="noResultsMessage" class="empty-state-message hidden">
                <i class="ph-binoculars"></i>
                <p>No applications match your search or filter.</p>
            </div>
        </div>

        <button id="openModalBtnMobile" class="btn-fab btn-primary sm:hidden" title="Add New Application">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="currentColor" viewBox="0 0 256 256">
                <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
            </svg>
        </button>

        <div id="toastNotification" class="fixed bottom-6 right-6 z-[70] text-white text-sm hidden opacity-0">
            <p id="toastMessage"></p>
        </div>
    `;

    // Add modals to the app
    const modalsContainer = document.createElement('div');
    modalsContainer.innerHTML = `
        <!-- Add/Edit Modal -->
        <div id="applicationModal" class="modal"></div>
        
        <!-- Delete Confirmation Modal -->
        <div id="deleteConfirmModal" class="confirm-modal"></div>
    `;
    appRoot.appendChild(modalsContainer);

    // Initialize managers
    const appManager = new ApplicationManager();
    let currentSortDirection = 'desc';

    // Initialize modal components
    const { openModal: openAddEditModal, populateForm } = initializeModal(appManager);
    const { showDeleteConfirm } = initializeDeleteConfirm(appManager);

    // Helper function to render applications
    const renderApplications = (applications: any[]) => {
        const applicationList = document.getElementById('applicationList');
        const noApplicationsMessage = document.getElementById('noApplicationsMessage');
        const noResultsMessage = document.getElementById('noResultsMessage');
        if (!applicationList || !noApplicationsMessage || !noResultsMessage) return;
        applicationList.innerHTML = '';
        noApplicationsMessage.classList.add('hidden');
        noResultsMessage.classList.add('hidden');
        const allApps = appManager.getAllApplications();
        if (!allApps || allApps.length === 0) {
            noApplicationsMessage.classList.remove('hidden');
            return;
        }
        if (!applications || applications.length === 0) {
            noResultsMessage.classList.remove('hidden');
            return;
        }
        applications.forEach((app: any, index: number) => {
            const card = createApplicationCard(app, index);
            applicationList.appendChild(card);
        });
    };

    // Filter and render applications
    const filterAndRender = () => {
        const searchInput = document.getElementById('searchInput') as HTMLInputElement | null;
        const statusFilterInput = document.getElementById('statusFilter') as HTMLSelectElement | null;
        const searchTerm = searchInput ? searchInput.value : '';
        const statusFilter = statusFilterInput ? statusFilterInput.value : '';
        const filtered = appManager.filterApplications(searchTerm, statusFilter, currentSortDirection);
        renderApplications(filtered);
    };

    // Subscribe to application changes
    appManager.subscribeToApplications((_applications: any[]) => {
        const loadingIndicator = document.getElementById('loadingIndicator') as HTMLElement | null;
        if (loadingIndicator) loadingIndicator.style.display = 'none';
        const stats = appManager.getStats();
        updateStats(stats);
        filterAndRender();
    });

    // Event listeners for controls
    const searchInput = document.getElementById('searchInput');
    if (searchInput) searchInput.addEventListener('input', filterAndRender);
    const statusFilterInput = document.getElementById('statusFilter');
    if (statusFilterInput) statusFilterInput.addEventListener('change', filterAndRender);
    const sortDateBtn = document.getElementById('sortDateBtn');
    if (sortDateBtn) sortDateBtn.addEventListener('click', () => {
        currentSortDirection = currentSortDirection === 'desc' ? 'asc' : 'desc';
        const icon = document.getElementById('sortDateIcon');
        if (icon) icon.className = `ph ph-sort-${currentSortDirection === 'desc' ? 'descending' : 'ascending'} text-xl`;
        filterAndRender();
    });
    const exportJsonBtn = document.getElementById('exportJsonBtn');
    if (exportJsonBtn) exportJsonBtn.addEventListener('click', () => {
        const allApps = appManager.getAllApplications();
        if (!allApps || allApps.length === 0) {
            showToast('No application data to export.', 'error');
            return;
        }
        appManager.exportToJSON();
    });
    // Event listeners for modal buttons
    const openModalBtn = document.getElementById('openModalBtn');
    if (openModalBtn) openModalBtn.addEventListener('click', () => openAddEditModal());
    const openModalBtnMobile = document.getElementById('openModalBtnMobile');
    if (openModalBtnMobile) openModalBtnMobile.addEventListener('click', () => openAddEditModal());
    // Delegate event listeners for application cards
    const applicationList = document.getElementById('applicationList');
    if (applicationList) applicationList.addEventListener('click', async (e: any) => {
        const target = e.target as HTMLElement;
        const card = target.closest('.application-card') as HTMLElement | null;
        if (!card) return;
        const button = target.closest('button') as HTMLElement | null;
        const appId = card.dataset.id || '';
        const companyName = card.dataset.company || "this application";
        if (button) {
            e.stopPropagation();
            if (button.classList.contains('calendar-btn')) {
                if (!appId) return;
                const app = appManager.getApplicationById(appId);
                if (app && app.dateApplied) {
                    const appliedDate = new Date(app.dateApplied);
                    const followUpDate = new Date(appliedDate);
                    followUpDate.setDate(appliedDate.getDate() + 7);
                    const startDateStr = formatDateForGoogleCalendar(followUpDate);
                    const endDate = new Date(followUpDate);
                    endDate.setDate(followUpDate.getDate() + 1);
                    const endDateStr = formatDateForGoogleCalendar(endDate);
                    const eventTitle = `Follow up: ${app.jobTitle || ''} at ${app.companyName || ''}`;
                    const eventDetails = `Follow up on job application.\nJob URL: ${app.jobUrl || 'N/A'}\nNotes: ${app.notes || ''}`;
                    const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startDateStr}/${endDateStr}&details=${encodeURIComponent(eventDetails)}&location=${encodeURIComponent(app.companyName || '')}`;
                    window.open(googleCalendarUrl, '_blank');
                }
            } else if (button.classList.contains('edit-btn')) {
                if (!appId) return;
                const app = appManager.getApplicationById(appId);
                if (app) {
                    openAddEditModal(true, appId);
                    populateForm(app);
                }
            } else if (button.classList.contains('delete-btn')) {
                if (!appId) return;
                showDeleteConfirm(appId, companyName);
            }
        } else {
            // Clicking on card opens edit modal
            if (!appId) return;
            const app = appManager.getApplicationById(appId);
            if (app) {
                openAddEditModal(true, appId);
                populateForm(app);
            }
        }
    });
});